"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { BookOpen, Camera, Sparkles, Compass } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
interface GalleryMoment {
  id: number;
  title: string;
  caption: string;
  location: string;
  src: string;
  srcset: string;
  sizes: string;
  aspect: string;
}
const moments: GalleryMoment[] = [{
  id: 1,
  title: "Twilight Toast",
  caption: "An intimate multi-generational dinner toast at twilight overlooking the Aegean Sea.",
  location: "Santorini Estate",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/gallery_twilight_toast.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/gallery_twilight_toast.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/gallery_twilight_toast.png-wvc-sizes",
  aspect: "aspect-[4/3] md:aspect-[16/10]"
}, {
  id: 2,
  title: "On the Teak Deck",
  caption: "Grandparents laughing with grandchildren on a private yacht deck at golden hour.",
  location: "Amalfi Coastline",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/gallery_boat_deck_laughter.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/gallery_boat_deck_laughter.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/gallery_boat_deck_laughter.png-wvc-sizes",
  aspect: "aspect-[4/5]"
}, {
  id: 3,
  title: "Coastal Discovery",
  caption: "Teenagers exploring hidden sea coves and turquoise shallows by private tender.",
  location: "Mallorcan Coves",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/gallery_coastal_cove_exploring.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/gallery_coastal_cove_exploring.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/gallery_coastal_cove_exploring.png-wvc-sizes",
  aspect: "aspect-[4/5]"
}, {
  id: 4,
  title: "Historic Stroll",
  caption: "Three generations walking together unhurriedly through a historic European courtyard.",
  location: "Lucca Piazza",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/gallery_european_courtyard_walk.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/gallery_european_courtyard_walk.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/gallery_european_courtyard_walk.png-wvc-sizes",
  aspect: "aspect-[4/3] md:aspect-[16/10]"
}];
const pillars = [{
  id: 1,
  icon: Camera,
  title: "Unobtrusive Presence",
  desc: "A seasoned documentary photojournalist moves discreetly alongside your itinerary, preserving unscripted moments."
}, {
  id: 2,
  icon: BookOpen,
  title: "Florentine Binding",
  desc: "Archival cotton paper bound in bespoke Italian full-grain leather, foil-stamped with your family crest."
}, {
  id: 3,
  icon: Sparkles,
  title: "Generational Heirlooms",
  desc: "Curated heirloom albums and private digital archives prepared for children and grandchildren to inherit."
}];
export default function LegacyLens() {
  const shouldReduceMotion = useReducedMotion();
  const easeCurve: [number, number, number, number] = [0.16, 1, 0.3, 1];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0 : 0.7,
        ease: easeCurve
      }
    }
  };
  return <section id="the-legacy-lens" data-nav="dark" className="dark bg-background text-foreground relative w-full overflow-hidden py-20 md:py-28 border-y border-border/30">
      {/* Subtle architectural background motifs */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-5">
        <div className="h-[680px] w-[680px] rounded-full border border-foreground/40" />
        <div className="absolute h-[940px] w-[940px] rounded-full border border-foreground/20" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-80px"
      }} variants={containerVariants} className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <motion.div variants={itemVariants} className="inline-flex items-center gap-2 mb-4">
            <span className="h-px w-6 bg-border" />
            <span className="font-sans text-xs tracking-[0.2em] uppercase text-muted-foreground">
              Documentary Keepsake
            </span>
            <span className="h-px w-6 bg-border" />
          </motion.div>

          <motion.h2 variants={itemVariants} className="font-serif text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight leading-[1.18] text-foreground mb-6">
            The Legacy Lens: Candid Storytelling
          </motion.h2>

          <motion.p variants={itemVariants} className="font-sans text-base sm:text-lg text-foreground/85 leading-relaxed max-w-2xl mx-auto">
            Our discreet documentary photojournalist embeds with your family to capture the raw, unscripted emotions of your journey, delivered in a custom, hand-crafted leather album designed by Italian bookbinders.
          </motion.p>
        </motion.div>

        {/* 4-Image Editorial Gallery */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-60px"
      }} variants={containerVariants} className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8 items-start mb-16 md:mb-20">
          {/* Top Row: Large landscape left (7 cols) + Tall portrait right (5 cols) */}
          <motion.div variants={itemVariants} className="md:col-span-7 group relative overflow-hidden rounded-xs border border-border/40 bg-card/10 shadow-lg">
            <div data-index={0} className="relative w-full overflow-hidden">
              <div className={moments[0].aspect}>
                <img src={moments[0].src} data-wvc-srcset={moments[0].srcset} data-wvc-sizes={moments[0].sizes} alt={moments[0].title} loading="lazy" decoding="async" className="h-full w-full object-cover grayscale-[15%] contrast-[1.05] transition-transform duration-700 ease-out group-hover:scale-[1.02]" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent pointer-events-none" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <div className="flex items-center gap-2 mb-1.5">
                  <Compass className="size-3.5 text-primary" />
                  <span className="font-sans text-xs tracking-wider uppercase text-muted-foreground">
                    {moments[0].location}
                  </span>
                </div>
                <h3 className="font-serif text-xl sm:text-2xl font-medium text-foreground mb-1">
                  {moments[0].title}
                </h3>
                <p className="font-sans text-xs sm:text-sm text-foreground/75 line-clamp-2 max-w-xl">
                  {moments[0].caption}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="md:col-span-5 group relative overflow-hidden rounded-xs border border-border/40 bg-card/10 shadow-lg">
            <div data-index={1} className="relative w-full overflow-hidden">
              <div className={moments[1].aspect}>
                <img src={moments[1].src} data-wvc-srcset={moments[1].srcset} data-wvc-sizes={moments[1].sizes} alt={moments[1].title} loading="lazy" decoding="async" className="h-full w-full object-cover grayscale-[15%] contrast-[1.05] transition-transform duration-700 ease-out group-hover:scale-[1.02]" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent pointer-events-none" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <div className="flex items-center gap-2 mb-1.5">
                  <Compass className="size-3.5 text-primary" />
                  <span className="font-sans text-xs tracking-wider uppercase text-muted-foreground">
                    {moments[1].location}
                  </span>
                </div>
                <h3 className="font-serif text-xl sm:text-2xl font-medium text-foreground mb-1">
                  {moments[1].title}
                </h3>
                <p className="font-sans text-xs sm:text-sm text-foreground/75 line-clamp-2">
                  {moments[1].caption}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Bottom Row: Tall portrait left (5 cols) + Large landscape right (7 cols) */}
          <motion.div variants={itemVariants} className="md:col-span-5 group relative overflow-hidden rounded-xs border border-border/40 bg-card/10 shadow-lg">
            <div data-index={2} className="relative w-full overflow-hidden">
              <div className={moments[2].aspect}>
                <img src={moments[2].src} data-wvc-srcset={moments[2].srcset} data-wvc-sizes={moments[2].sizes} alt={moments[2].title} loading="lazy" decoding="async" className="h-full w-full object-cover grayscale-[15%] contrast-[1.05] transition-transform duration-700 ease-out group-hover:scale-[1.02]" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent pointer-events-none" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <div className="flex items-center gap-2 mb-1.5">
                  <Compass className="size-3.5 text-primary" />
                  <span className="font-sans text-xs tracking-wider uppercase text-muted-foreground">
                    {moments[2].location}
                  </span>
                </div>
                <h3 className="font-serif text-xl sm:text-2xl font-medium text-foreground mb-1">
                  {moments[2].title}
                </h3>
                <p className="font-sans text-xs sm:text-sm text-foreground/75 line-clamp-2">
                  {moments[2].caption}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="md:col-span-7 group relative overflow-hidden rounded-xs border border-border/40 bg-card/10 shadow-lg">
            <div data-index={3} className="relative w-full overflow-hidden">
              <div className={moments[3].aspect}>
                <img src={moments[3].src} data-wvc-srcset={moments[3].srcset} data-wvc-sizes={moments[3].sizes} alt={moments[3].title} loading="lazy" decoding="async" className="h-full w-full object-cover grayscale-[15%] contrast-[1.05] transition-transform duration-700 ease-out group-hover:scale-[1.02]" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent pointer-events-none" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <div className="flex items-center gap-2 mb-1.5">
                  <Compass className="size-3.5 text-primary" />
                  <span className="font-sans text-xs tracking-wider uppercase text-muted-foreground">
                    {moments[3].location}
                  </span>
                </div>
                <h3 className="font-serif text-xl sm:text-2xl font-medium text-foreground mb-1">
                  {moments[3].title}
                </h3>
                <p className="font-sans text-xs sm:text-sm text-foreground/75 line-clamp-2 max-w-xl">
                  {moments[3].caption}
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Curation Details & Features Matrix */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-60px"
      }} variants={containerVariants} className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-10 border-t border-border/40 mb-14">
          {pillars.map((pillar, i) => {
          const IconComp = pillar.icon;
          return <motion.div key={pillar.id} variants={itemVariants} className="p-6 md:p-8 rounded-xs border border-border/30 bg-background/50 hover:border-primary/40 transition-[border-color] duration-300">
                <div data-index={i} className="flex flex-col h-full">
                  <div className="inline-flex size-10 items-center justify-center rounded-xs bg-primary/15 text-primary mb-5">
                    <IconComp className="size-5" />
                  </div>
                  <h4 className="font-serif text-lg font-medium text-foreground mb-2">
                    {pillar.title}
                  </h4>
                  <p className="font-sans text-sm text-foreground/75 leading-relaxed mt-auto">
                    {pillar.desc}
                  </p>
                </div>
              </motion.div>;
        })}
        </motion.div>

        {/* CTA Footer Callout */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-40px"
      }} variants={itemVariants} className="flex flex-col sm:flex-row items-center justify-between gap-6 p-8 md:p-10 rounded-xs border border-border/40 bg-card/5">
          <div className="space-y-1 text-center sm:text-left">
            <h3 className="font-serif text-xl sm:text-2xl font-medium text-foreground">
              Commission The Legacy Lens
            </h3>
            <p className="font-sans text-sm text-muted-foreground max-w-xl">
              Embed a private visual chronicler into your next milestone expedition.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-4 shrink-0">
            <Button asChild size="lg" className="rounded-xs tracking-wider uppercase text-xs font-semibold px-8">
              <Link to="/AboutDiscoveryConsultationPage">
                Schedule Discovery Call
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}